import {
  type UseDocumentCapabilityProps,
  useDocumentCapability,
} from './useDocumentCapability';

export type DocumentQueueGroupsAccess = {
  /**
   * Whether queue groups are available for this document — the `queueGroups`
   * HATEOAS capability, gated by `FeatureType::QueueGroups` on the organisation.
   */
  hasGroupsAccess: boolean;
  /**
   * Whether the underlying document capability has settled SUCCESSFULLY. Until
   * then `hasGroupsAccess` is `false` only because the document has not loaded
   * yet (or its fetch failed and left no data), NOT because the feature is off —
   * consumers must gate cold-cache reads on this so the capability flip
   * (`false -> true`) is observed instead of latched. A failed fetch keeps this
   * `false`: reporting the (unknown) access as final would open the gate with
   * `hasGroupsAccess=false` and latch a group-less workflow whose save wipes the
   * document's groups (PER-3430).
   */
  isResolved: boolean;
};

/**
 * Queue-groups access signal for the workflow-designer chain: `hasGroupsAccess`
 * plus whether that answer is final (`isResolved`). use-workflow-document gates its
 * cold-cache read on `isResolved` and keys on `hasGroupsAccess`, both from this one
 * instance; the modal reads `hasGroupsAccess` for the designer-context UI.
 *
 * Scope note: the document-queue signing-steps chain does NOT consume this hook —
 * its call sites (WorkflowTab, DocumentSummary) read `queueGroups` straight from the
 * capability and recover from the cold cache via the shared `queueGroupsAccessKeySegment`
 * key flip, not an `isResolved` gate. So this is the single source for the WORKFLOW
 * chain's resolve gate, not a shared gate across both paths — do not assume one
 * `isResolved` check protects the signing-steps path.
 *
 * INVARIANT: `hasGroupsAccess` and `isResolved` are derived from the SAME
 * `useDocumentCapability` instance, so `isResolved` flips to `true` exactly when
 * `hasGroupsAccess` reaches its final, successfully-fetched value. Reading both
 * from one hook keeps that invariant structural instead of relying on two
 * separate hooks happening to share the `['document', id]` cache entry.
 *
 * Location: kept in `src/hooks/document/` next to its only dependency,
 * `useDocumentCapability`; moving it to `entities/` while that dependency stays in
 * legacy would add a new `entities -> src/hooks` edge (regressing the FSD graph).
 * Promoting the whole `src/hooks/document/` module is a separate ticket.
 */
export const useDocumentQueueGroupsAccess = (
  props: UseDocumentCapabilityProps,
): DocumentQueueGroupsAccess => {
  const { queueGroups, isLoading, isError } = useDocumentCapability(props);

  return {
    hasGroupsAccess: queueGroups,
    isResolved: !isLoading && !isError,
  };
};
